import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from "axios";
import { APIURL } from "../../Global";
import { Modal } from 'react-bootstrap';
import SimpleBar from 'simplebar-react';
import FormView from "./FormView";
import { formatDateToDDMMYYYY } from "../common/DateUtils";

const VISITOR_CATEGORIES = {
    RELATIVE: 'relative',
    FRIEND: 'friend',
    GENERAL: 'general',
    ORGANISATION: 'organisation'
};

function ApprovedRejected({ status }) {
    const [visitors, setVisitors] = useState({
        all: [],
        relative: [],
        friend: [],
        general: [],
        organisation: []
    });

    const [filteredVisitors, setFilteredVisitors] = useState({
        relative: [],
        friend: [],
        general: [],
        organisation: []
    });

    const [currentCategory, setCurrentCategory] = useState(VISITOR_CATEGORIES.RELATIVE);
    const [searchTerm, setSearchTerm] = useState("");
    const [formShow, setFormShow] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        setSearchTerm('')     
    }, [status])

    const authToken = useMemo(() => {
        const tokenString = sessionStorage.getItem("usertoken");
        return tokenString?.replace(/["]+/g, "") || "";
    }, []);

    // Fetch visitors data
    const fetchVisitors = useCallback(async () => {
        if (!authToken) {
            setError("Authentication token not found");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await axios.get(
                `${APIURL}/api/v1/service-provider/visitor-list/?status=${status}`,
                {
                    headers: { Authorization: `Token ${authToken}` }
                }
            );

            if (response.status === 200) {
                const allVisitors = response.data.visitors || [];

                const categorizedVisitors = {
                    all: allVisitors,
                    relative: allVisitors.filter(v => v.visitor_type === VISITOR_CATEGORIES.RELATIVE),
                    friend: allVisitors.filter(v => v.visitor_type === VISITOR_CATEGORIES.FRIEND),
                    general: allVisitors.filter(v => v.visitor_type === VISITOR_CATEGORIES.GENERAL),
                    organisation: allVisitors.filter(v => v.visitor_type === VISITOR_CATEGORIES.ORGANISATION)
                };

                setVisitors(categorizedVisitors);

                // Initialize filtered visitors with all data
                setFilteredVisitors({
                    relative: categorizedVisitors.relative,
                    friend: categorizedVisitors.friend,
                    general: categorizedVisitors.general,
                    organisation: categorizedVisitors.organisation
                });
            }
        } catch (err) {
            setError("Failed to fetch visitors data");
            console.error("Error fetching visitors:", err);
        } finally {
            setLoading(false);
        }
    }, [status, authToken]);

    useEffect(() => {
        fetchVisitors();
    }, [fetchVisitors]);

    // Enhanced search functionality that filters all categories
    const performSearch = useCallback((term) => {
        if (!term.trim()) {
            // If search is empty, show all visitors for each category
            setFilteredVisitors({
                relative: visitors.relative,
                friend: visitors.friend,
                general: visitors.general,
                organisation: visitors.organisation
            });
            return;
        }

        const normalizedTerm = term.toLowerCase().trim();

        // Filter each category based on search term
        const searchInCategory = (categoryVisitors) => {
            return categoryVisitors.filter(visitor => {
                const fullName = `${visitor.first_name || ''} ${visitor.last_name || ''}`.toLowerCase();
                const visitorId = visitor.id?.toString().toLowerCase() || '';
                const purpose = visitor.purpose?.toLowerCase() || '';

                return fullName.includes(normalizedTerm) ||
                    visitorId.includes(normalizedTerm) ||
                    visitor.first_name?.toLowerCase().includes(normalizedTerm) ||
                    visitor.last_name?.toLowerCase().includes(normalizedTerm) ||
                    purpose.includes(normalizedTerm);
            }).sort((a, b) => {
                const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
                const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
                return nameA.localeCompare(nameB);
            });
        };

        setFilteredVisitors({
            relative: searchInCategory(visitors.relative),
            friend: searchInCategory(visitors.friend),
            general: searchInCategory(visitors.general),
            organisation: searchInCategory(visitors.organisation)
        });
    }, [visitors]);

    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchTerm(value);
        performSearch(value);
    }, [performSearch]);

    const handleCategoryChange = useCallback((category) => {
        setCurrentCategory(category);
    }, []);

    const handleFormShow = useCallback((patient) => {
        setSelectedPatient(patient);
        setFormShow(true);
    }, []);

    const handleFormClose = useCallback(() => {
        setFormShow(false);
        setSelectedPatient({});
    }, []);

    // Form Modal Component
    const FormPopUp = useCallback(({ show, onHide }) => (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            aria-labelledby="contained-modal-title-vcenter"
            centered
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header closeButton />
            <Modal.Body>
                <FormView patient={selectedPatient} />
            </Modal.Body>
        </Modal>
    ), [selectedPatient]);

    const formatVisitorName = useCallback((visitor) =>
        `${visitor.first_name || ''} ${visitor.last_name || ''}`.trim(),
        []
    );

    const formatPurpose = useCallback((purpose) =>
        purpose?.replace(/_/g, " ") || "N/A",
        []
    );

    const formatDateRange = useCallback((fromDate, toDate) => {
        if (!fromDate) return "N/A";

        const formattedFromDate = formatDateToDDMMYYYY(fromDate);

        if (toDate && toDate !== fromDate) {
            return (
                <>
                    <span style={{ color: "blue" }}>{formattedFromDate}</span>
                    --
                    <span style={{ color: "blue" }}>{formatDateToDDMMYYYY(toDate)}</span>
                </>
            );
        }

        return <span style={{ color: "blue" }}>{formattedFromDate}</span>;
    }, []);

    const getStatusIconColor = useCallback((visitorStatus) =>
        visitorStatus === "approved" ? "rgba(94, 203, 91, 1)" : "rgba(229, 89, 11, 1)",
        []
    );

    const getCategoryDisplayName = useCallback((category) => {
        const names = {
            [VISITOR_CATEGORIES.RELATIVE]: 'Relative',
            [VISITOR_CATEGORIES.FRIEND]: 'Friend',
            [VISITOR_CATEGORIES.GENERAL]: 'General',
            [VISITOR_CATEGORIES.ORGANISATION]: 'Organisation'
        };
        return names[category] || category;
    }, []);

    const renderCategoryTabs = () => (
        <div className="col-6" style={{ display: 'flex', padding: 0 }}>
            {Object.values(VISITOR_CATEGORIES).map((category) => {
                const count = filteredVisitors[category]?.length || 0;
                const isActive = currentCategory === category;

                return (
                    <div
                        key={category}
                        onClick={() => handleCategoryChange(category)}
                        style={{
                            textAlign: "center",
                            padding: "1.2%",
                            color: isActive ? "#FFFFFF" : "black",
                            backgroundColor: isActive ? "#6d4482" : "#f8f9fa",
                            cursor: "pointer",
                            borderRadius: "10px",
                            margin: "0 2px",
                            flex: 1,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            minHeight: "40px"
                        }}
                    >
                        <span style={{ flex: 1 }}>
                            {getCategoryDisplayName(category)}
                        </span>
                        <span style={{
                            fontSize: "12px",
                            fontWeight: "bold",
                            backgroundColor: isActive ? "rgba(255,255,255,0.2)" : "rgba(109,68,130,0.1)",
                            padding: "2px 6px",
                            borderRadius: "10px",
                            minWidth: "20px"
                        }}>
                            {count}
                        </span>
                    </div>
                );
            })}
        </div>
    );

    const renderSearchBox = () => (
        <div className="col-5" style={{ padding: 0 }}>
            <div className="searchboxdeathrepo" style={{ borderRadius: '10px' }}>
                <input
                    placeholder="Search Name, ID or Purpose"
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    style={{
                        marginLeft: '10px',
                        marginTop: "3px",
                        border: "0",
                        outline: "none",
                        width: "88%",
                    }}
                />
                <i className="fa fa-search" aria-hidden="true" />
            </div>
        </div>
    );

    const renderVisitorRow = (visitor) => (
        <div key={visitor.id} style={{ paddingRight: "1%" }} className="deathreq_lists row">
            <div className="col-5" style={{ padding: "1%", fontSize: "18px", textTransform: "capitalize" }}>
                &nbsp;&nbsp;{formatVisitorName(visitor)}
            </div>

            <div className="col-2" style={{ paddingLeft: "2%" }}>
                <div style={{ border: "1px solid rgba(205, 205, 205, 1)", borderRadius: "5px", width: "70%" }} className="row">
                    <div
                        style={{
                            borderRight: "1px solid rgba(158, 158, 158, 1)",
                            color: "rgba(79, 135, 203, 1)",
                            padding: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                        className="col-6"
                    >
                        <i
                            className="fa fa-envelope"
                            aria-hidden="true"
                            style={{
                                fontSize: "20px",
                                color: getStatusIconColor(visitor.status)
                            }}
                        />
                    </div>
                    <div style={{ paddingTop: "7%" }} className="col-6">
                        <i
                            onClick={() => handleFormShow(visitor)}
                            className="fa fa-eye"
                            style={{
                                fontSize: "23px",
                                marginLeft: "5%",
                                color: "rgba(36, 86, 147, 1)",
                                cursor: "pointer"
                            }}
                            aria-hidden="true"
                        />
                    </div>
                </div>
            </div>

            <div className="col-2" style={{ padding: "1%", textTransform: "capitalize" }}>
                &nbsp;&nbsp;&nbsp;{formatPurpose(visitor.purpose)}
            </div>

            <div className="col-3" style={{ padding: "1%" }}>
                &nbsp;&nbsp;&nbsp;{formatDateRange(visitor.from_date, visitor.to_date)}
            </div>
        </div>
    );

    const getCurrentDisplayedVisitors = () => {
        return filteredVisitors[currentCategory] || [];
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="leftdivdeathrepo" style={{ padding: "1%", minHeight: "460px", marginBottom: "1%" }}>
                    <div style={{ marginTop: "10%", color: "#6D4482", textAlign: "center" }}>
                        Loading visitors...
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="leftdivdeathrepo" style={{ padding: "1%", minHeight: "460px", marginBottom: "1%" }}>
                    <div style={{ marginTop: "10%", color: "red", textAlign: "center" }}>
                        {error}
                    </div>
                </div>
            );
        }

        const currentVisitors = getCurrentDisplayedVisitors();

        return (
            <div className="leftdivdeathrepo" style={{ padding: "1%", minHeight: "460px", marginBottom: "1%" }}>
                {currentVisitors.length > 0 && (
                    <div className="row">
                        <label className="visitor-text-style-here col-5" />
                        <label className="visitor-text-style-here col-2">Application Form</label>
                        <label className="visitor-text-style-here col-2">Purpose of Visit</label>
                        <label className="visitor-text-style-here col-3">Date</label>
                    </div>
                )}

                <SimpleBar style={{ maxHeight: '800px', paddingLeft: "2%", paddingRight: "2%", marginTop: "1%" }}>
                    {currentVisitors.length > 0 ? (
                        currentVisitors.map(renderVisitorRow)
                    ) : (
                        <div style={{ marginTop: "10%", color: "red", textAlign: "center" }}>
                            {searchTerm
                                ? `No matching ${getCategoryDisplayName(currentCategory).toLowerCase()} visitors found!`
                                : `No ${getCategoryDisplayName(currentCategory)} Visitors!!`
                            }
                        </div>
                    )}
                </SimpleBar>
            </div>
        );
    };

    return (
        <>
            <div style={{ display: 'flex', margin: "2% 0 1%" }}>
                {renderCategoryTabs()}
                <div className="col-1" />
                {renderSearchBox()}
            </div>

            {renderContent()}

            <FormPopUp show={formShow} onHide={handleFormClose} />
        </>
    );
}

export default ApprovedRejected;